import { useContext, useEffect } from "react"

import { graphql, PageProps } from "gatsby"

import Layout from "components/Layout"
import { Lightmode } from "components/Providers"
import CTA from "components/sectionTemplates/CTA"
import SEO from "components/Seo"
import Hero from "sections/b2b/01-Hero"
import B2BLargeStat from "sections/b2b/02-B2BLargeStat"
import Testimonials from "sections/b2b/04-Testimonials"
import B2BCarousel from "sections/b2b/06-B2BCarousel"
import TheProblem from "sections/b2b/3-TheProblem"
import TheSolution from "sections/b2b/4-TheSolution"
import DigitalFirst from "sections/b2b/7-DigitalFirst"
import Innovations from "sections/b2b/8-Innovation"
import BluePenguin from "sections/b2b/9-BluePenguin"
import colors from "styles/colors"
import { QueryB2BPage } from "types/gatsby-alias"
import { isBrowser, translateSeo } from "utils/functions"
import useMedia from "utils/useMedia"
import ExpenseManagement from "sections/b2b/ExpenseManagement"

interface Props extends PageProps {
  data: QueryB2BPage
}

export default function B2B({ data, location }: Props) {
  const { setLightmode } = useContext(Lightmode)

  useEffect(() => {
    if (isBrowser()) {
      setLightmode(true)
    }
  }, [setLightmode])

  const { loan, deposits, reader, loanM, depositsM, readerM } = data

  const allCarouselImages = {
    loanVal: loan?.childrenImageSharp?.[0]?.gatsbyImageData,
    loansValM: loanM?.childrenImageSharp?.[0]?.gatsbyImageData,
    depositsVal: deposits?.childrenImageSharp?.[0]?.gatsbyImageData,
    depositsMVal: depositsM?.childrenImageSharp?.[0]?.gatsbyImageData,
    readerVal: reader?.childrenImageSharp?.[0]?.gatsbyImageData,
    readerMVal: readerM?.childrenImageSharp?.[0]?.gatsbyImageData,
  }

  const { bluePenguinPerson, bluePenguinPersonM } = data

  const bluePenguinImage = useMedia(
    bluePenguinPerson?.childrenImageSharp?.[0]?.gatsbyImageData,
    bluePenguinPerson?.childrenImageSharp?.[0]?.gatsbyImageData,
    bluePenguinPerson?.childrenImageSharp?.[0]?.gatsbyImageData,
    bluePenguinPersonM?.childrenImageSharp?.[0]?.gatsbyImageData
  )

  return (
    <Layout
      id="b2b"
      waveBottomColor={colors.seaFoam800}
      hideOverflow={useMedia(false, false, true, true)}
      pathname={location.pathname}
    >
      <Hero />
      <B2BLargeStat />
      <TheProblem />
      <TheSolution />
      <Testimonials />
      <B2BCarousel allCarouselImages={allCarouselImages} />
      <DigitalFirst />
      <ExpenseManagement />
      <Innovations />
      {bluePenguinImage && <BluePenguin image={bluePenguinImage} />}

      <CTA />
    </Layout>
  )
}

export function Head({ location, data }: Props) {
  const title = "B2B Payments Platform Adapts to Modern Commercial Banking"
  const description =
    "Create entirely new payment solutions not contemplated with other issuing processors or B2B payment providers."
  const { translatedTitle, translatedDesc } = translateSeo(
    data.locales.edges[0]?.node.data ?? null,
    title,
    description
  )
  return (
    <SEO
      pageAttributes={{
        title: translatedTitle,
        description: translatedDesc,
        id: "b2b",
        url: location.pathname,
        originalPath: location.pathname,
      }}
    />
  )
}

export const query = graphql`
  query B2BPage($language: String!) {
    locales: allLocale(
      filter: { ns: { in: ["b2b-payments"] }, language: { eq: $language } }
    ) {
      edges {
        node {
          ns
          data
          language
        }
      }
    }
    bluePenguinPerson: file(
      name: { eq: "bluePenguinPerson" }
      sourceInstanceName: { eq: $language }
    ) {
      name
      childrenImageSharp {
        gatsbyImageData(formats: WEBP, placeholder: BLURRED)
      }
    }
    bluePenguinPersonM: file(
      name: { eq: "bluePenguinPersonM" }
      sourceInstanceName: { eq: $language }
    ) {
      name
      childrenImageSharp {
        gatsbyImageData(formats: WEBP, placeholder: BLURRED)
      }
    }
    loanM: file(
      name: { eq: "SolutionsLoanM" }
      sourceInstanceName: { eq: $language }
    ) {
      name
      childrenImageSharp {
        gatsbyImageData(formats: WEBP, placeholder: BLURRED)
      }
    }
    loan: file(
      name: { eq: "SolutionsLoan" }
      sourceInstanceName: { eq: $language }
    ) {
      name
      childrenImageSharp {
        gatsbyImageData(formats: WEBP, placeholder: BLURRED)
      }
    }
    reader: file(
      name: { eq: "SolutionsReader" }
      sourceInstanceName: { eq: $language }
    ) {
      name
      childrenImageSharp {
        gatsbyImageData(formats: WEBP, placeholder: BLURRED)
      }
    }
    readerM: file(
      name: { eq: "SolutionsReaderM" }
      sourceInstanceName: { eq: $language }
    ) {
      name
      childrenImageSharp {
        gatsbyImageData(formats: WEBP, placeholder: BLURRED)
      }
    }
    depositsM: file(
      name: { eq: "SolutionsDepositsM" }
      sourceInstanceName: { eq: $language }
    ) {
      name
      childrenImageSharp {
        gatsbyImageData(formats: WEBP, placeholder: BLURRED)
      }
    }
    deposits: file(
      name: { eq: "SolutionsDeposits" }
      sourceInstanceName: { eq: $language }
    ) {
      name
      childrenImageSharp {
        gatsbyImageData(formats: WEBP, placeholder: BLURRED)
      }
    }
  }
`
